import * as React from "react";
import CashGiftPayment from "./modals/CashGiftPayment";
import ForeignDetails from "./modals/ForeignDetails";
import Icon from "@src/components/Icon";
import {
  Currency,
  EventSupportCurrencyProvider,
  GetForeignAccountDetailsQuery,
} from "@src/@types/api.d";
import { IconType } from "@src/utils/icon";
import { AvailablePaymentCurrencies } from "@src/components/BirthdayTemplate1/AsoEbiLink";

export type ForeignAccountDetailsType =
  GetForeignAccountDetailsQuery["getForeignAccountDetails"];

export type ForeignAccountDetailType =
  GetForeignAccountDetailsQuery["getForeignAccountDetails"][number];

interface CashGiftModalProps {
  eventId: number;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  foreignDetails: ForeignAccountDetailsType | undefined;
  eventUuid: string | undefined;
  isCashGiftSetup: boolean;
  availableGiftPaymentCurrencies?: AvailablePaymentCurrencies;
}

// type CashGiftModalInterface = "foreign" | "naira" | "option";
type CashGiftModalInterface = "stripe" | "paystack" | "flutterwave" | "option";

interface CashGiftOptionInterface {
  setView: (i: CashGiftModalInterface) => void;
  foreignDetails: ForeignAccountDetailsType | undefined;
  setForeignDetail: React.Dispatch<
    React.SetStateAction<ForeignAccountDetailType>
  >;
  isCashGiftSetup: boolean;
  availableGiftPaymentCurrencies?: AvailablePaymentCurrencies;
  setForeignCurrency: React.Dispatch<React.SetStateAction<Currency | null>>;
  setCurrencyToPay: React.Dispatch<React.SetStateAction<Currency | null>>;
}

// type currencyTypes = "usd" | "gbp" | "ghs" | "zar" | "ngn";

// export const currencyNames: Record<currencyTypes, string> = {
//   gbp: "Pound",
//   usd: "Dollar",
//   ghs: "Cedis",
//   zar: "Rand",
//   ngn: "Naira",
// };

const CashGiftOption = ({
  setView,
  availableGiftPaymentCurrencies,
  foreignDetails,
  setForeignDetail,
  isCashGiftSetup,
  setForeignCurrency,
  setCurrencyToPay,
}: CashGiftOptionInterface) => {
  // Merge enabled currencies and foreign details currencies into a unique array
  const enabledCurrencies = React.useMemo(() => {
    return (
      availableGiftPaymentCurrencies?.map(
        (currency) => currency?.currency as Currency
      ) || []
    );
  }, [availableGiftPaymentCurrencies]);

  const allCurrencies = React.useMemo(() => {
    const foreignCurrencies =
      foreignDetails?.map((detail) => detail?.currency) || [];
    const combined = [...enabledCurrencies, ...foreignCurrencies];
    // Remove duplicates by converting to Set and back to array
    return Array.from(new Set(combined));
  }, [enabledCurrencies, foreignDetails]);

  const handleCurrencyClick = (currency: Currency) => {
    setCurrencyToPay(currency);
    // Find all entries for this currency in availableGiftPaymentCurrencies
    const currencyConfigs =
      availableGiftPaymentCurrencies?.filter(
        (item) => item?.currency === currency
      ) || [];

    // Check if any entry has PAYSTACK provider
    const hasPaystack = currencyConfigs.some(
      (config) => config?.provider === EventSupportCurrencyProvider.Paystack
    );

    // Check if any entry has FLUTTERWAVE provider
    const hasFlutterwave = currencyConfigs.some(
      (config) => config?.provider === EventSupportCurrencyProvider.Flutterwave
    );

    if (hasPaystack) {
      setView("paystack");
    } else if (hasFlutterwave) {
      setView("flutterwave");
    } else {
      // Find the foreign detail for this currency
      setForeignCurrency(currency);
      const detail = foreignDetails?.find((d) => d?.currency === currency);
      if (detail) {
        setForeignDetail(detail);
      }
      setView("stripe");
    }
  };

  return (
    <div className="w-full rounded-md bg-white p-8 sm:w-[500px] max-h-[90vh] overflow-y-auto thin-scrollbar">
      <h1 className="mb-8 text-2xl font-bold text-[black] font-inter">
        Send money
      </h1>

      <div className="flex flex-wrap items-start justify-center gap-8">
        {allCurrencies.map((currency, index) => {
          // const hasIcon = ["ngn", "usd", "gbp"].includes(
          //   currency?.toLowerCase()
          // );
          const hasIcon = false;
          return (
            <div
              key={index}
              className="flex w-[75px] flex-col items-center justify-center gap-2"
            >
              <div
                className="flex h-[100px] w-[68px] cursor-pointer items-center justify-center rounded-md border border-gray-300 transition duration-500 hover:ring-1 hover:ring-[#008636]"
                onClick={() => handleCurrencyClick(currency as Currency)}
              >
                {hasIcon ? (
                  <span className="text-gray-300">
                    <Icon iconName={currency as IconType} />
                  </span>
                ) : (
                  <div className="grid h-[40px] w-[40px] place-items-center rounded-full bg-[#D4D4D8] text-xs font-semibold font-inter uppercase text-white">
                    {currency?.toUpperCase()}
                  </div>
                )}
              </div>
              <span className="text-center text-sm text-[#707070] font-inter">
                Send {currency?.toLocaleUpperCase()}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CashGiftModal = ({
  eventId,
  setOpen,
  foreignDetails,
  eventUuid,
  isCashGiftSetup,
  availableGiftPaymentCurrencies,
}: CashGiftModalProps) => {
  const [cashGiftView, setCashGiftView] =
    React.useState<CashGiftModalInterface>("option");

  const [foreignDetail, setForeignDetail] =
    React.useState<ForeignAccountDetailType>(null);

  const [foreignCurrency, setForeignCurrency] = React.useState<Currency | null>(
    null
  );

  const [currencyToPay, setCurrencyToPay] = React.useState<Currency | null>(
    null
  );

  // React.useEffect(() => {
  //   if (availableGiftPaymentCurrencies?.length === 0) {
  //     setCashGiftView("naira");
  //   }
  // }, [availableGiftPaymentCurrencies]);

  const view: Record<CashGiftModalInterface, JSX.Element> = {
    stripe: (
      <ForeignDetails
        foreignCurrency={foreignCurrency}
        foreignDetail={foreignDetail}
        eventId={eventId}
        eventUuid={`${eventUuid}`}
        setOpen={setOpen}
        availableGiftPaymentCurrencies={availableGiftPaymentCurrencies}
      />
    ),
    paystack: (
      <CashGiftPayment
        eventUuid={eventUuid}
        eventId={eventId}
        setOpen={setOpen}
        provider={EventSupportCurrencyProvider.Paystack}
        currencyToPay={currencyToPay}
      />
    ),
    flutterwave: (
      <CashGiftPayment
        eventUuid={eventUuid}
        eventId={eventId}
        setOpen={setOpen}
        provider={EventSupportCurrencyProvider.Flutterwave}
        currencyToPay={currencyToPay}
      />
    ),
    option: (
      <CashGiftOption
        availableGiftPaymentCurrencies={availableGiftPaymentCurrencies}
        foreignDetails={foreignDetails}
        setView={(i) => setCashGiftView(i)}
        setForeignDetail={setForeignDetail}
        setForeignCurrency={setForeignCurrency}
        isCashGiftSetup={isCashGiftSetup}
        setCurrencyToPay={setCurrencyToPay}
      />
    ),
  };

  return view[cashGiftView];
};

export const MemoCashGiftModal = React.memo(CashGiftModal);
